import * as React from "react"
import EditBillingUserInfo from "@/routes/_home/user-role-management/billing-user-info/edit"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { BillingUserInfoType } from "@/types/billing"
import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteBillingUserInfo } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import { Dialog } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import BookForm from "@/components/forms/book-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface DataProps {
  data: BillingUserInfoType
}

export function BillingUserInfoOperation({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showEditModal, setShowEditModal] = React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  return (
    <>
      <div>
        <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
          <DropdownMenuTrigger>
            <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
              <Icons.ellipsis className="size-4" />
              <span className="sr-only">Open</span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => {
                setShowEditModal(!showEditModal)
                setShowDropDown(false)
              }}
            >
              <Icons.edit className="mr-2 size-4" />
              Edit Billig User Info
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="text-destructive"
              onSelect={() => {
                validateUserDeletePermission()
                setShowDeleteModal(true)
                setShowDropDown(false)
              }}
            >
              <Icons.delete className="mr-2 size-4" />
              Delete Billing User Info
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Edit Billing User Info */}
        <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
          <EditBillingUserInfo
            billingUserInfo={data}
            setShowEditBillingUserInfoModal={setShowEditModal}
          />
        </Credenza>

        {/* Delete Billing User Info */}
        <DeleteBillingUserInfo
          billingUser={data.username}
          billingUserId={data.id}
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
        />
      </div>
    </>
  )
}

function DeleteBillingUserInfo({
  billingUser,
  billingUserId,
  showDeleteModal,
  setShowDeleteModal,
}: {
  billingUser?: string
  billingUserId?: number
  showDeleteModal: boolean
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const formSchema = z.object({
    selected_billing_user: z
      .string()
      .min(1, "Required")
      .refine((val) => val === billingUser, {
        message: "Selected user must match the user.",
      }),
  })
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: billingUserId,
      selected_billing_user: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (billingUserId) {
        return await deleteBillingUserInfo(billingUserId)
      }
    },
    onSuccess: () => {
      toast({
        description: "The billing user has been successfully deleted.",
      })

      setShowDeleteModal(!showDeleteModal)
      queryClient.invalidateQueries({ queryKey: ["billingUserInfoDetails"] })
    },
  })

  function handleDelete() {
    console.log(billingUserId)

    mutation.mutate()
  }

  return (
    <Credenza open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(handleDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Billing User</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="selected_billing_user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type "{billingUser}" to delete.</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
